/**
 * FlowCanvasFrame — the full-bleed wrapper around <ReactFlow>. Split from
 * SitemapCanvas because the web AddDrag hit-test has to ask "is this element
 * inside the flow canvas?" — the frame registers its element in a module-level
 * WeakSet (react-strict-dom's html.div forwards no `data-*` hook beyond
 * `data-testid`), and `isInFlowCanvas(el)` walks the ancestors against it.
 */
import { useCallback, type ReactNode } from "react";
import { html, css } from "react-strict-dom";

const flowCanvases = new WeakSet<Element>();

/** Mark `el` as a flow-canvas frame. Returns the unregister function. */
export function registerFlowCanvas(el: Element): () => void {
  flowCanvases.add(el);
  return () => {
    flowCanvases.delete(el);
  };
}

/** True when `el` is a registered flow-canvas frame or sits inside one. */
export function isInFlowCanvas(el: Element | null | undefined): boolean {
  for (let node = el ?? null; node; node = node.parentElement) {
    if (flowCanvases.has(node)) return true;
  }
  return false;
}

const s = css.create({
  frame: { width: "100%", height: "100%" },
});

export function FlowCanvasFrame({ children }: { children: ReactNode }) {
  // A callback ref (React 19: its return value is the cleanup) keeps the
  // registration exactly as long as the element is mounted.
  const register = useCallback(
    (el: HTMLDivElement | null) => (el ? registerFlowCanvas(el) : undefined),
    [],
  );
  return (
    <html.div ref={register} style={s.frame}>
      {children}
    </html.div>
  );
}
